import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  AnalystProgressResponse,
  AnalystTurnStatus,
  AskChatRequest,
  ChatMessageResponse,
  ChatMessageWireResponse,
  ChatResponse,
  ChatWithMessagesResponse,
  ChatWithMessagesWireResponse,
  CreateChatRequest,
  CreditBalanceResponse,
  CreditConsumptionEntry,
  CreditLedgerEntryWireResponse,
  GenerateReportRequest,
  PatchChatRequest,
  ReportJobStatus,
  SiteCreditBalanceWireResponse,
  SubmitFeedbackRequest,
} from 'src/app/models/chat.model';
import { isGuid } from 'src/app/_helpers/guid.helper';
import { ChatMessageExportFormat } from 'src/app/enums/chatMessageExportFormat.enum';
import { CreditLedgerEntryType } from 'src/app/enums/creditLedgerEntryType.enum';
import { ChatService } from 'src/app/services/chat.service';
import { ToastService } from 'src/app/services/toast.service';
import { CREDIT_BALANCE_GRID_PAGE_SIZE, CREDIT_CONSUMPTION_GRID_PAGE_SIZE, RECENT_CONSUMPTION_LIMIT } from 'src/app/shared/constants/chat.constants';
import { GridModel } from 'src/app/shared/grid/grid.model';
import { buildGridParams } from 'src/app/shared/utils/grid-params.util';
import { sanitizeCitationRoute } from 'src/app/shared/utils/citation-resolver.util';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatHttpService extends ChatService {
  private readonly _creditBalance$ = new BehaviorSubject<CreditBalanceResponse | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly toastService: ToastService,
  ) {
    super();
  }

  createChat(request: CreateChatRequest): Observable<ChatResponse> {
    return this.http.post<ChatResponse>(`${environment.apiUrl}/Chat/Create`, request);
  }

  getTurnProgress(chatId: string): Observable<AnalystProgressResponse> {
    const params = new HttpParams().set('chatId', chatId);
    return this.http.get<AnalystProgressResponse>(`${environment.apiUrl}/Chat/GetTurnProgress`, { params }).pipe(
      catchError(() => of({ phase: null })),
    );
  }

  submitAsk(request: AskChatRequest): Observable<ReportJobStatus> {
    return this.http.post<ReportJobStatus>(`${environment.apiUrl}/Chat/SubmitAsk`, request);
  }

  getTurnStatus(jobId: string): Observable<AnalystTurnStatus> {
    const params = new HttpParams().set('jobId', jobId);
    return this.http.get<AnalystTurnStatus>(`${environment.apiUrl}/Chat/GetTurnStatus`, { params });
  }

  cancelTurn(jobId: string): Observable<void> {
    if (!isGuid(jobId)) {
      return EMPTY;
    }
    return this.http.post<void>(`${environment.apiUrl}/Chat/CancelTurn/${jobId}`, null);
  }

  generateReport(request: GenerateReportRequest): Observable<ReportJobStatus> {
    return this.http.post<ReportJobStatus>(`${environment.apiUrl}/Chat/GenerateReport`, request);
  }

  getReportJob(jobId: string): Observable<ReportJobStatus> {
    const params = new HttpParams().set('id', jobId);
    return this.http.get<ReportJobStatus>(`${environment.apiUrl}/Chat/GetReportJob`, { params });
  }

  cancelReportJob(jobId: string): Observable<void> {
    if (!isGuid(jobId)) {
      return EMPTY;
    }
    return this.http.post<void>(`${environment.apiUrl}/Chat/CancelReportJob/${jobId}`, null);
  }

  getChats(): Observable<ChatResponse[]> {
    return this.http.get<ChatResponse[]>(`${environment.apiUrl}/Chat/GetChats`).pipe(
      catchError((error: unknown) => {
        this.toastService.showError(error);
        return of([]);
      }),
    );
  }

  getChatWithMessages(id: string): Observable<ChatWithMessagesResponse> {
    const params = new HttpParams().set('id', id);
    return this.http
      .get<ChatWithMessagesWireResponse>(`${environment.apiUrl}/Chat/GetWithMessages`, { params })
      .pipe(
        map((response) => this.mapChatWithMessagesResponse(response)),
        catchError((error: unknown) => {
          this.toastService.showError(error);
          return EMPTY;
        }),
      );
  }

  patchChat(id: string, request: PatchChatRequest): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}/Chat/Patch/${id}`, request);
  }

  deleteChat(id: string): Observable<void> {
    const params = new HttpParams().set('id', id);
    return this.http.delete<void>(`${environment.apiUrl}/Chat/Delete`, { params });
  }

  get creditBalance$(): Observable<CreditBalanceResponse | null> {
    return this._creditBalance$.asObservable();
  }

  getCreditBalance(): Observable<CreditBalanceResponse> {
    return this.http
      .get<GridModel<SiteCreditBalanceWireResponse>>(`${environment.apiUrl}/SiteCreditBalance/GetList`, {
        params: buildGridParams('', '', '', 0, CREDIT_BALANCE_GRID_PAGE_SIZE, ''),
      })
      .pipe(
        map((result) => ({ balance: result.data.reduce((sum, row) => sum + row.balance, 0) })),
        tap((result) => this._creditBalance$.next(result)),
        catchError((error: unknown) => {
          this.toastService.showError(error);
          return EMPTY;
        }),
      );
  }

  refreshCreditBalance(): void {
    this.getCreditBalance().subscribe();
  }

  getRecentConsumption(): Observable<CreditConsumptionEntry[]> {
    return this.http
      .get<GridModel<CreditLedgerEntryWireResponse>>(`${environment.apiUrl}/CreditLedger/GetList`, {
        params: buildGridParams('', 'dateCreated', 'desc', 0, CREDIT_CONSUMPTION_GRID_PAGE_SIZE, ''),
      })
      .pipe(
        map((result) =>
          result.data
            .filter((row) => row.entryType === CreditLedgerEntryType.Drawdown)
            .slice(0, RECENT_CONSUMPTION_LIMIT)
            .map((row) => ({ id: row.id, credits: Math.abs(row.amount), dateCreated: row.dateCreated })),
        ),
        catchError((error: unknown) => {
          this.toastService.showError(error);
          return of([]);
        }),
      );
  }

  submitFeedback(request: SubmitFeedbackRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/Chat/SubmitFeedback`, request);
  }

  clearFeedback(messageId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/Chat/ClearFeedback/${messageId}`);
  }

  exportMessage(messageId: string, format: ChatMessageExportFormat): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}/Chat/messages/${messageId}/export/${format}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  private mapChatWithMessagesResponse(response: ChatWithMessagesWireResponse): ChatWithMessagesResponse {
    const { messages, ...chat } = response;
    return { ...chat, messages: messages.map((message) => this.mapMessage(message)) };
  }

  private mapMessage(message: ChatMessageWireResponse): ChatMessageResponse {
    const { author, ...rest } = message;
    return {
      ...rest,
      role: author,
      kind: message.kind,
      citations: (message.citations ?? []).map((citation) => ({
        ...citation,
        route: sanitizeCitationRoute(citation.route),
      })),
      outputs: message.outputs ?? [],
      rating: message.rating ?? null,
      stopReason: null,
      hasIncompleteData: false,
    };
  }
}
